import React from "react";
import { motion } from "framer-motion";

export default function NoInternetPage({ onRetry }) {
  return (
    <div className="relative min-h-screen overflow-hidden bg-black text-white flex items-center justify-center px-6!">
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-red-500/20 blur-3xl rounded-full animate-pulse" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-cyan-500/20 blur-3xl rounded-full animate-pulse delay-300" />
      </div>

      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <span
            key={i}
            className="absolute w-2 h-2 bg-white/20 rounded-full animate-bounce"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${i * 0.2}s`,
              animationDuration: `${2 + (i % 3)}s`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-5xl w-full grid lg:grid-cols-2 gap-14 items-center">
        <div className="text-center lg:text-left">
          <div className="inline-flex items-center gap-2 px-4! py-2! rounded-full border border-white/20 bg-white/5 backdrop-blur-md mb-6!">
            <div className="w-2 h-2 rounded-full bg-red-500 animate-ping" />
            <span className="text-sm tracking-wide text-gray-300">Connection Lost</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-black leading-tight">
            No <span className="text-red-500">Internet</span>
            <br />
            Connection
          </h1>

          <p className="mt-6! text-gray-400 text-lg leading-relaxed max-w-xl">
            Your digital bridge seems disconnected. Check your Wi‑Fi or mobile data and reconnect to continue your journey.
          </p>

          <div className="mt-10! flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
            <button
              onClick={onRetry}
              className="px-8! py-4! rounded-2xl bg-red-500 hover:bg-red-600 transition-all duration-300 font-semibold shadow-[0_0_30px_rgba(239,68,68,0.4)] hover:scale-105 cursor-pointer"
            >
              Retry Connection
            </button>
          </div>
        </div>

        <div className="flex justify-center items-center relative">
          <div className="relative w-[320px] h-[320px] md:w-[420px] md:h-[420px] flex items-center justify-center">
            <div className="absolute w-full h-full border border-red-500/20 rounded-full animate-ping" />
            <div className="absolute w-[80%] h-[80%] border border-cyan-500/20 rounded-full animate-pulse" />

            <div className="relative flex flex-col items-center justify-center">
              <div className="absolute w-56 h-56 bg-red-500/10 blur-3xl rounded-full" />

              <motion.div
                initial={{ scale: 0.7, opacity: 0, rotate: -10 }}
                animate={{
                  scale: [1, 1.08, 1],
                  opacity: [0.7, 1, 0.7],
                  rotate: [0, 3, -3, 0],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="relative"
              >
                <motion.div
                  className="absolute inset-0 rounded-full border border-cyan-400/30"
                  animate={{ scale: [1, 1.5], opacity: [0.5, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />

                <motion.div
                  className="absolute inset-0 rounded-full border border-red-400/30"
                  animate={{ scale: [1, 1.8], opacity: [0.4, 0] }}
                  transition={{ duration: 2.5, repeat: Infinity }}
                />

                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-44 h-44 text-white drop-shadow-[0_0_25px_rgba(255,255,255,0.5)]"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8.288 15.038a5.25 5.25 0 0 1 7.424 0M5.106 11.856c3.514-3.514 9.213-3.514 12.728 0M1.924 8.674c5.272-5.272 13.82-5.272 19.092 0M12 20.25h.008v.008H12v-.008Z"
                  />
                </svg>

                <motion.div
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="absolute -bottom-4 -right-4 w-16 h-16 rounded-full bg-red-500 flex items-center justify-center shadow-[0_0_40px_rgba(239,68,68,0.6)]"
                >
                  <span className="text-4xl font-black">×</span>
                </motion.div>
              </motion.div>

              <div className="absolute bottom-10 right-2 w-16 h-16 rounded-full bg-red-500 flex items-center justify-center shadow-[0_0_40px_rgba(239,68,68,0.5)] animate-bounce">
                <span className="text-4xl font-black">×</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
